"use client";

import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { useEffect, useRef, type ReactNode } from "react";
import { THEME_COOKIE, COLOR_THEME_COOKIE, LOGO_HOVER_COOKIE, THEME_TRANSITION_COOKIE, writeThemeCookie, COOKIE_MAX_AGE } from "../lib/theme-cookie";
import { shouldAdoptInitialTheme } from "../lib/theme-sync";
import { installClientLogger } from "../lib/telemetry/client-logger";
import { useEmbeddedThemeBridge } from "../hooks/useEmbeddedThemeBridge";
import { EMBED_HUELLE_CSS, embedAdresseVorpaintSkript, embedVorpaintSkript } from "../lib/embed-vorpaint";
import { embedFlaecheVorpaintSkript } from "../lib/embed-flaeche";
import { getPublicEnv } from "@janeway/app-sdk/public-env";

interface ThemeProviderProps {
  children: ReactNode;
  /** Initialer Wert vom Server (Cookie). Verhindert Hydration-Flash. */
  initialTheme?: "light" | "dark";
  /** Fallback falls kein Cookie/localStorage gesetzt. Default: "dark" */
  defaultTheme?: "light" | "dark" | "system";
  /** System-Preference erlauben. Default: false */
  enableSystem?: boolean;
}

/** Der Seed setzt hierauf den bereinigten Cookie-Wert — der SSR-Wert kann veraltet sein. */
const SEED_GLOBAL = "__janewayThemeCookie";

/**
 * Inline-Script, laeuft VOR dem next-themes-Script.
 *
 * Zwei Aufgaben:
 *
 * 1. **Host-only Doppelgaenger ausraeumen.** Vor dem Cookie-Domain-Fix
 *    (2026-07-05) schrieben Apps host-only Cookies mit einem Jahr Laufzeit. Sie
 *    liegen NEBEN dem geteilten Parent-Domain-Cookie; `document.cookie` liefert
 *    beide, und die Lese-Regex nimmt den zuerst gesetzten — den veralteten. Der
 *    Server kann sie nicht unterscheiden (im `Cookie`-Header steht keine Domain),
 *    deshalb kann auch `initialTheme` aus dem SSR falsch sein. Nach dem Purge
 *    steht der bereinigte Wert in `window.__janewayThemeCookie` und schlaegt
 *    `initialTheme`.
 *
 * 2. **localStorage seeden.** next-themes nimmt per-origin `localStorage['theme']`
 *    als Source-of-Truth und liest unseren Cross-App-Cookie nie selbst.
 */
export function buildSeedScript(domain: string | undefined): string {
  const d = JSON.stringify(domain ?? "");
  // THEME_TRANSITION_COOKIE gehoert seit seiner Einfuehrung in dieselbe Liste:
  // ein host-only Doppelgaenger wuerde beim Lesen gewinnen und ueber
  // writeThemeTransition() in den geteilten Cookie aller Subdomains befoerdert.
  const names = JSON.stringify([THEME_COOKIE, COLOR_THEME_COOKIE, LOGO_HOVER_COOKIE, THEME_TRANSITION_COOKIE]);
  return `(function(){try{
var D=${d},N=${names},T=${JSON.stringify(THEME_COOKIE)};
function rd(n){var m=document.cookie.match(new RegExp('(?:^|; )'+n+'=([^;]*)'));return m?decodeURIComponent(m[1]):undefined;}
function wr(n,v){var p=n+'='+encodeURIComponent(v)+'; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax';if(D)p+='; Domain='+D;if(location.protocol==='https:')p+='; Secure';document.cookie=p;}
if(D&&window.self===window.top){for(var i=0;i<N.length;i++){var n=N[i],before=rd(n);
  document.cookie=n+'=; Path=/; Max-Age=0';
  var after=rd(n);
  var v=after!==undefined?after:before;
  if(v!==undefined)wr(n,v);}}
var t=rd(T);
if(t==='light'||t==='dark'){localStorage.setItem('theme',t);window.${SEED_GLOBAL}=t;}
}catch(e){}})();`.replace(/\n/g, "");
}

/**
 * Haelt den geteilten `janeway-theme`-Cookie und next-themes konsistent:
 *  - Reconcile-on-mount: zwingt next-themes auf den SSR-Cookie-Wert, falls ein
 *    stale per-origin localStorage abweicht (Garantie auch ohne Seed-Script).
 *  - Persistenz: schreibt User-Aenderungen in den Cookie, ueberspringt aber den
 *    ersten (= initialen) Wert, damit nie ein stale localStorage in den geteilten
 *    Cookie zurueck-„echoed" wird (das war die Ursache, dass hell global zu dunkel
 *    ueberschrieben wurde).
 */
function ThemeCookieSync({ initialTheme }: { initialTheme?: "light" | "dark" }) {
  const { resolvedTheme, setTheme } = useTheme();

  // Client-Error-Telemetrie global aktivieren (idempotent, browser-only).
  // ThemeProvider wird in JEDER App gemountet -> ein zentraler Hook reicht.
  useEffect(() => {
    installClientLogger();
  }, []);

  const reconciled = useRef(false);
  useEffect(() => {
    if (reconciled.current || !resolvedTheme) return;
    reconciled.current = true;
    // Der SSR-Wert kann aus einem host-only Doppelgaenger stammen (der Server sieht
    // keine Cookie-Domain). Das Seed-Script hat den bereinigt und hier abgelegt.
    const seeded = (window as unknown as Record<string, unknown>)[SEED_GLOBAL];
    const authoritative = seeded === "light" || seeded === "dark" ? (seeded as string) : initialTheme;
    if (shouldAdoptInitialTheme(authoritative, resolvedTheme)) {
      setTheme(authoritative as string);
    }
  }, [initialTheme, resolvedTheme, setTheme]);

  const firstWrite = useRef(true);
  useEffect(() => {
    if (resolvedTheme !== "light" && resolvedTheme !== "dark") return;
    if (firstWrite.current) {
      firstWrite.current = false;
      return;
    }
    // Diese Zeile war bis 0.442.1 die fuenfzehnte Schreibstelle, von der
    // `ColorThemeProvider` behauptete, es gebe sie nicht: sie schrieb den
    // Hell/Dunkel-Keks auf `.janewaylabs.com` ungeprueft — auch aus einer
    // eingebetteten App und auch aus dem abgeschotteten Showroom. Sie steht
    // unveraendert da; die Pruefung ist in `writeThemeCookie` gewandert.
    writeThemeCookie(THEME_COOKIE, resolvedTheme);
  }, [resolvedTheme]);

  return null;
}

export function ThemeProvider({
  children,
  initialTheme,
  defaultTheme = "dark",
  enableSystem = false,
}: ThemeProviderProps) {
  // Laeuft die App im iframe einer anderen Oekosystem-App, folgt sie deren Theme-Wechseln.
  // Der geteilte `janeway-theme`-Cookie (Domain .janewaylabs.com) sorgt schon beim LADEN
  // fuer Gleichstand; diese Bruecke haelt ein bereits geladenes iframe synchron, wenn der
  // Nutzer im Parent umschaltet. Ausserhalb eines iframes ein No-Op.
  useEmbeddedThemeBridge(getPublicEnv("NEXT_PUBLIC_COOKIE_DOMAIN"));

  return (
    <>
      {/* DIE HUELLE IM EMBED, BEVOR SIE GEZEICHNET WIRD — und zwar HIER, weil
          jede App diesen Provider mountet.

          Bis 0.442.0 stand das Paar in `AppLayout`. Das deckte sieben der neun
          Apps, die das Seitenpanel einbettet, und genau zwei nicht: **ai-chat
          und notes bauen eine eigene Huelle** (ihr `components/AppRahmen.tsx`
          sagt es woertlich: „Eigene Shell statt `AppLayout`") und rendern
          `TopNav` unmittelbar. Zweifach gemessen am 2026-09-17:

            · kein `AppLayout`/`AppRahmen` im Quelltext beider Apps
            · `data-jw-eingebettet` im gebauten Buendel: 2 Treffer bei
              calendar/contacts/mail/tasks/drive/immo/automations, **0 bei
              ai-chat und notes** — der Baum schuettelt den ungenutzten
              Baustein heraus.

          Beide blenden ihre Huelle im Embed ausserdem NIE aus: `useIsEmbedded`
          kommt in ihrem Rahmen nicht vor. Die CSS-Regel ist dort also nicht
          bloss der frueher greifende Weg, sondern der EINZIGE.

          `ThemeProvider` steht in jeder Wurzel-`layout.tsx` (bei calendar ueber
          `RootShellGate`), gibt schon heute ein Inline-Skript als erstes Kind
          aus und ist damit vor jedem Huellen-Markup. Eine Zeile je App waere
          die Bringschuld, vor der `AppLayout` selbst gewarnt hat.

          WAS DABEI WEGFAELLT: `AppLayout` gab das Paar nur bei automatischer
          Erkennung aus (`embedded === undefined`), damit der Schalter
          „eingebettet" im Showroom in einem iframe abschaltbar bleibt. Die
          Bedingung entfaellt hier — gemessen, nicht angenommen: die Prop wird
          an genau zwei Stellen gesetzt (`app/shell/ShowroomRahmen.tsx`,
          `janeway-drive/app/bearbeiten/[nodeId]/page.tsx`), und die
          Showroom-Seite laeuft selbst in KEINEM Rahmen. Das Skript setzt die
          Marke also gar nicht erst, die Regel bleibt wirkungslos, und der
          Schalter tut weiter, was er soll. */}
      <style dangerouslySetInnerHTML={{ __html: EMBED_HUELLE_CSS }} />
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: embedVorpaintSkript() }} />
      {/* DIE ZWEITE HAELFTE DERSELBEN MECHANIK — und sie wurde bis 0.442.1
          NIRGENDS ausgegeben.

          `embedFlaecheVorpaintSkript` gibt es seit dem 2026-09-16: exportiert,
          mit 127 Zeilen gruener Tests darueber, **ohne einen einzigen
          Aufrufer** (gemessen: kein Treffer ausser Definition und Export). Die
          Kopfzeilen-Kur gab `AppLayout` aus, die Flaechen-Kur niemand. Der
          Quelltext daneben sagt, was das kostet: „Ohne das Skript schliesst
          diese Zeile die Naht 224–293 ms nach dem ersten Bild statt sofort."

          Halb eingebaute Mechanik mit voller Testabdeckung darueber ist die
          unangenehmste Sorte: der Waechter ist gruen, weil die Funktion stimmt
          — er sagt nichts darueber, ob sie je laeuft. */}
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: embedFlaecheVorpaintSkript() }} />
      {/* DIE DRITTE HAELFTE — die Adresse.

          Marke und Flaeche halten, solange das Dokument steht. Was sie NICHT
          ueberleben, ist ein zweites Laden derselben Adresse, nachdem die
          Gast-App sie neu geschrieben hat: `embed`, `flaeche`, `farbthema` und
          `wirt` sind dann weg, und die App baut ihre volle Huelle mit dem
          eigenen Ton auf. Messung und die betroffenen Apps stehen bei
          `embedAdresseVorpaintSkript`.

          VOR dem Framework-Bundle, weil Nexts App-Router seinen eigenen Griff
          um `history.pushState` legt — er ruft den vor, den er vorfindet. */}
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: embedAdresseVorpaintSkript() }} />
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: buildSeedScript(getPublicEnv("NEXT_PUBLIC_COOKIE_DOMAIN")) }} />
      {/* Verborgen geladen = kein Auftritt, alles in Ruhelage (G5, `lib/auftritt-vorpaint.ts`). */}
      <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: auftrittVorpaintSkript() }} />
      <NextThemesProvider
        attribute="class"
        defaultTheme={initialTheme ?? defaultTheme}
        enableSystem={enableSystem}
        disableTransitionOnChange
      >
        <ThemeCookieSync initialTheme={initialTheme} />
        {children}
      </NextThemesProvider>
    </>
  );
}
